import { createContext, useContext } from 'react';

import type { FeatureFlagMapDto } from './endpoints';

/**
 * Context plumbing for the feature-flag map, split from
 * `FeatureFlagsProvider.tsx` so that file only exports components
 * (react-refresh constraint).
 */

/**
 * Query key for the per-user flag map. Shared by the provider,
 * `FeatureFlagsRouteSync` (invalidate-on-navigation), and any explicit
 * invalidation after an admin toggles a flag.
 */
export const FEATURE_FLAGS_QUERY_KEY = ['feature-flags'] as const;

export const FeatureFlagsContext = createContext<FeatureFlagMapDto | null>(null);

/**
 * The raw per-user flag map. Prefer `useFeature` for individual flags; this is
 * the escape hatch for diagnostics and tests.
 *
 * @throws Error outside a `FeatureFlagsProvider`
 */
export function useFeatureFlagMap(): FeatureFlagMapDto {
  const flags = useContext(FeatureFlagsContext);
  if (flags === null) {
    throw new Error('useFeatureFlagMap must be used within a FeatureFlagsProvider');
  }
  return flags;
}
